
import React, { lazy, Suspense, useEffect } from 'react';
import { Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useAuth } from './contexts/AuthContext';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import ProtectedRoute from './components/Auth/ProtectedRoute';
import { Toaster } from './components/ui/toaster';
import './App.css';
import { Skeleton } from './components/ui/skeleton';

// Create a client with caching configuration
const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 5 * 60 * 1000, // 5 minutes
      cacheTime: 10 * 60 * 1000, // 10 minutes
      retry: 1,
      refetchOnWindowFocus: false,
    },
  },
});

// Lazy load components
const Index = lazy(() => import('./pages/Index'));
const Auth = lazy(() => import('./pages/Auth'));
const Account = lazy(() => import('./pages/Account'));
const Profile = lazy(() => import('./pages/Profile'));
const OrderDetail = lazy(() => import('./pages/OrderDetail'));
const Medicines = lazy(() => import('./pages/Medicines'));
const ProductDetail = lazy(() => import('./pages/ProductDetail'));
const Cart = lazy(() => import('./pages/Cart'));
const Checkout = lazy(() => import('./pages/Checkout'));
const Contact = lazy(() => import('./pages/Contact'));
const Terms = lazy(() => import('./pages/Terms'));
const RefundPolicy = lazy(() => import('./pages/RefundPolicy'));
const PrivacyPolicy = lazy(() => import('./pages/PrivacyPolicy'));
const ShippingPolicy = lazy(() => import('./pages/ShippingPolicy'));
const NotFound = lazy(() => import('./pages/NotFound'));
const Search = lazy(() => import('./pages/Search'));
const Stores = lazy(() => import('./pages/Stores'));
const StoreDashboard = lazy(() => import('./pages/StoreDashboard'));
const UploadPrescription = lazy(() => import('./pages/UploadPrescription'));
const PrescriptionConfirmation = lazy(() => import('./pages/PrescriptionConfirmation'));
const Blog = lazy(() => import('./pages/Blog'));
const BlogDetail = lazy(() => import('./pages/BlogDetail'));
const PaymentStatus = lazy(() => import('./pages/PaymentStatus'));

// Lazy load admin pages
const AdminDashboard = lazy(() => import('./pages/AdminDashboard'));
const AdminOrders = lazy(() => import('./pages/admin/AdminOrders'));
const AdminOrderDetail = lazy(() => import('./pages/admin/OrderDetail'));
const AdminInventory = lazy(() => import('./pages/admin/AdminInventory'));
const ProductEdit = lazy(() => import('./pages/admin/ProductEdit'));
const ProductAdd = lazy(() => import('./pages/admin/ProductAdd'));
const AdminUsers = lazy(() => import('./pages/admin/AdminUsers'));
const UserProfile = lazy(() => import('./pages/admin/UserProfile'));
const AdminProfile = lazy(() => import('./pages/admin/AdminProfile'));
const AdminStores = lazy(() => import('./pages/admin/AdminStores'));
const AdminStoreDetail = lazy(() => import('./pages/admin/StoreDetail'));
const StoreAdd = lazy(() => import('./pages/admin/StoreAdd'));
const StoreEdit = lazy(() => import('./pages/admin/StoreEdit'));
const AdminAnalytics = lazy(() => import('./pages/admin/AdminAnalytics'));
const AdminSettings = lazy(() => import('./pages/admin/AdminSettings'));
const AdminCategories = lazy(() => import('./pages/admin/AdminCategories'));
const CategoryEdit = lazy(() => import('./pages/admin/CategoryEdit'));
const AdminPrescriptions = lazy(() => import('./pages/admin/AdminPrescriptions'));
const AdminTestimonials = lazy(() => import('./pages/admin/AdminTestimonials'));

// Loading component for suspense fallback
const PageLoader = () => (
  <div className="flex items-center justify-center min-h-screen bg-background">
    <div className="w-full max-w-md p-8 space-y-4">
      <Skeleton className="h-12 w-3/4 mx-auto" />
      <Skeleton className="h-32 w-full" />
      <div className="space-y-2">
        <Skeleton className="h-4 w-full" />
        <Skeleton className="h-4 w-5/6" />
        <Skeleton className="h-4 w-4/6" />
      </div>
    </div>
  </div>
);

// Updated default favicon URL
const defaultFaviconUrl = '/lovable-uploads/83ec9808-b2ca-42ab-a9fd-11f7f2029bb1.png';

function App() {
  const { loading, user } = useAuth();
  const location = useLocation();

  useEffect(() => {
    // Track page views with Facebook Pixel when route changes
    if (window.fbq) {
      window.fbq('track', 'PageView');
    }
  }, [location.pathname]);

  useEffect(() => {
    // Force favicon refresh on app load to ensure it's properly updated
    const refreshFavicon = () => {
      try {
        // Get favicon from localStorage or use default
        const storedFavicon = localStorage.getItem('instamg-favicon') || defaultFaviconUrl;
        
        // Update the main favicon element
        const faviconElement = document.getElementById('dynamic-favicon') as HTMLLinkElement;
        if (faviconElement) {
          faviconElement.href = storedFavicon;
        }
        
        // Create a new favicon element with cache busting
        const link = document.createElement('link');
        link.rel = 'shortcut icon';
        link.href = storedFavicon + '?v=' + new Date().getTime();
        
        // Remove any existing dynamically added favicon elements
        document.querySelectorAll('link[rel="icon"]:not(#dynamic-favicon), link[rel="shortcut icon"]').forEach(el => {
          document.head.removeChild(el);
        });
        
        // Add the new element
        document.head.appendChild(link);
        
        console.log('App.tsx: Favicon refreshed to:', storedFavicon);
      } catch (err) {
        console.error('Error refreshing favicon in App.tsx:', err);
      }
    };
    
    // Run favicon refresh
    refreshFavicon();
    
    // Also refresh favicon when app becomes visible
    document.addEventListener('visibilitychange', () => {
      if (document.visibilityState === 'visible') {
        refreshFavicon();
      }
    });
    
    return () => {
      document.removeEventListener('visibilitychange', refreshFavicon);
    };
  }, []);

  if (loading) {
    return <PageLoader />;
  }

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <Suspense fallback={<PageLoader />}>
        <Routes>
          <Route path="/" element={<Index />} />
          <Route path="/auth" element={<Auth />} />
          <Route path="/account" element={<ProtectedRoute><Account /></ProtectedRoute>} />
          <Route path="/profile" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/orders" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/profile/orders/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
          <Route path="/orders" element={<ProtectedRoute><Profile /></ProtectedRoute>} />
          <Route path="/orders/:orderId" element={<ProtectedRoute><OrderDetail /></ProtectedRoute>} />
          <Route path="/medicines" element={<Medicines />} />
          <Route path="/product/:id" element={<ProductDetail />} />
          <Route path="/medicines/:id" element={<ProductDetail />} />
          <Route path="/cart" element={<Cart />} />
          <Route path="/checkout" element={<ProtectedRoute><Checkout /></ProtectedRoute>} />
          <Route path="/payment/status/:orderId" element={<PaymentStatus />} />
          <Route path="/contact" element={<Contact />} />
          <Route path="/terms" element={<Terms />} />
          <Route path="/refund-policy" element={<RefundPolicy />} />
          <Route path="/privacy-policy" element={<PrivacyPolicy />} />
          <Route path="/shipping-policy" element={<ShippingPolicy />} />
          <Route path="/search" element={<Search />} />
          <Route path="/stores" element={<Stores />} />
          <Route path="/store-dashboard" element={<StoreDashboard />} />
          <Route path="/upload-prescription" element={<UploadPrescription />} />
          <Route path="/prescription-confirmation" element={<PrescriptionConfirmation />} />
          
          {/* Blog Routes */}
          <Route path="/blog" element={<Blog />} />
          <Route path="/blog/:blogId" element={<BlogDetail />} />
          
          <Route path="*" element={<NotFound />} />
          
          {/* Admin Routes */}
          <Route path="/admin" element={<Navigate to="/admin/dashboard" replace />} />
          <Route path="/admin/dashboard" element={<ProtectedRoute allowedRoles={['admin']}><AdminDashboard /></ProtectedRoute>} />
          <Route path="/admin/profile" element={<ProtectedRoute allowedRoles={['admin']}><AdminProfile /></ProtectedRoute>} />
          <Route path="/admin/orders" element={<ProtectedRoute allowedRoles={['admin']}><AdminOrders /></ProtectedRoute>} />
          <Route path="/admin/orders/:orderId" element={<ProtectedRoute allowedRoles={['admin']}><AdminOrderDetail /></ProtectedRoute>} />
          <Route path="/admin/inventory" element={<ProtectedRoute allowedRoles={['admin']}><AdminInventory /></ProtectedRoute>} />
          <Route path="/admin/inventory/add" element={<ProtectedRoute allowedRoles={['admin']}><ProductAdd /></ProtectedRoute>} />
          <Route path="/admin/inventory/:productId" element={<ProtectedRoute allowedRoles={['admin']}><ProductEdit /></ProtectedRoute>} />
          <Route path="/admin/product/edit/:productId" element={<ProtectedRoute allowedRoles={['admin']}><ProductEdit /></ProtectedRoute>} />
          <Route path="/admin/users" element={<ProtectedRoute allowedRoles={['admin']}><AdminUsers /></ProtectedRoute>} />
          <Route path="/admin/users/:userId" element={<ProtectedRoute allowedRoles={['admin']}><UserProfile /></ProtectedRoute>} />
          <Route path="/admin/stores" element={<ProtectedRoute allowedRoles={['admin']}><AdminStores /></ProtectedRoute>} />
          <Route path="/admin/stores/:storeId" element={<ProtectedRoute allowedRoles={['admin']}><AdminStoreDetail /></ProtectedRoute>} />
          <Route path="/admin/stores/add" element={<ProtectedRoute allowedRoles={['admin']}><StoreAdd /></ProtectedRoute>} />
          <Route path="/admin/stores/edit/:storeId" element={<ProtectedRoute allowedRoles={['admin']}><StoreEdit /></ProtectedRoute>} />
          <Route path="/admin/analytics" element={<ProtectedRoute allowedRoles={['admin']}><AdminAnalytics /></ProtectedRoute>} />
          <Route path="/admin/settings" element={<ProtectedRoute allowedRoles={['admin']}><AdminSettings /></ProtectedRoute>} />
          <Route path="/admin/categories" element={<ProtectedRoute allowedRoles={['admin']}><AdminCategories /></ProtectedRoute>} />
          <Route path="/admin/categories/:categoryId" element={<ProtectedRoute allowedRoles={['admin']}><CategoryEdit /></ProtectedRoute>} />
          <Route path="/admin/prescriptions" element={<ProtectedRoute allowedRoles={['admin']}><AdminPrescriptions /></ProtectedRoute>} />
          <Route path="/admin/testimonials" element={<ProtectedRoute allowedRoles={['admin']}><AdminTestimonials /></ProtectedRoute>} />
        </Routes>
      </Suspense>
    </QueryClientProvider>
  );
}

// Add fbq to the window interface
declare global {
  interface Window {
    fbq: any;
  }
}

export default App;
